import "App.scss"
import queryString from "query-string"
import theme from "./styles/theme"
import React, { createContext, useState, useEffect } from "react"
import { Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom"
import { Header } from "components/Header"
import { ProductsPage } from "pages/ProductsPage"
import { Product } from "pages/Product"
import { OrderPage } from "pages/OrderPage"
import { Payments } from "pages/Payments"
import { RestaurantSelector } from "components/RestaurantSelector"
import { NotFoundRestaurant } from "components/NotFoundRestaurant"
import { ThemeProvider } from "@mui/material/styles"
import { LoadingOverlay } from "components/LoadingOverlay"
import { PhoneSimulator } from "components/PhoneSimulator"
import { Box, useMediaQuery } from "@mui/material"
import { getDishes, getSettings, getTypes, getGroups, getReastaurant } from "helpers/api"
import useLocalStorage from "hooks/useLocalStorage"
import NoAccessMessage from "components/NoAccessMessage"

const getInitialLanguage = () => {
  const savedLang = localStorage.getItem("language")
  if (savedLang) return JSON.parse(savedLang)

  const browserLang = navigator.language.split("-")[0] // "ru-RU" → "ru"
  return browserLang || "en"
}
export const CartContext = createContext()

// Компонент для защиты маршрутов
const ProtectedRoute = ({ children }) => {
  const savedQueryId = localStorage.getItem('queryId');
  const savedUser = localStorage.getItem('user');
  
  if (!savedQueryId && !savedUser) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export function App() {
  const [queryId, setQueryId] = useState(() => {
    const savedQueryId = localStorage.getItem('queryId');
    return savedQueryId || "000";
  });
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const navigate = useNavigate();

  // Сохраняем состояние аутентификации в localStorage при изменении
  useEffect(() => {
    if (queryId) {
      localStorage.setItem('queryId', queryId);
    }
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }
  }, [queryId, user]);

  // Проверяем аутентификацию при загрузке
  useEffect(() => {
    const savedQueryId = localStorage.getItem('queryId');
    const savedUser = localStorage.getItem('user');
    
    if (!savedQueryId && !savedUser) {
      // Если нет сохраненных данных, перенаправляем на начальную страницу
      navigate('/');
    }
  }, []);

  const location = useLocation()
  const query = queryString.parse(location.search)

  const [foods, setFoods] = useLocalStorage("foods", [])
  const [loading, setLoading] = useState(true)
  const [cartItems, setCartItems] = useLocalStorage("cartItems", [])
  const [totalPrice, setTotalPrice] = useLocalStorage("totalPrice", 0)
  const [customerName, setCustomerName] = useLocalStorage("customer_name", "")
  const [comment, setComment] = useLocalStorage("comment", "")
  const [address, setAddress] = useLocalStorage("address", "")
  const [telephone, setTelephone] = useLocalStorage("telephone", "")
  const [optionDelivery, setOptionDelivery] = useLocalStorage("optionDelivery", "on_site")
  const [paymentMethod, setPaymentMethod] = useLocalStorage("paymentMethod", "")
  const [restaurant, setRestaurant] = useState({})
  const [settings, setSettings] = useState({})
  const [typesList, setTypesList] = useState([])
  const [groupsList, setGroupsList] = useState([])

  const [language, setLanguage] = useLocalStorage("language", getInitialLanguage())
  const [restaurant_id, setRestaurant_id] = useLocalStorage("restaurant_id", null)
  const [qr_name, setQrName] = useLocalStorage("qr_name", null) // добавляем qr_name

  useEffect(() => {
    if (!language) {
      const userLanguages = navigator.languages
      if (userLanguages && userLanguages.length > 0) {
        setLanguage(userLanguages[0].slice(0, 2))
      }
    }

    if (!restaurant_id) {
      setRestaurant_id(query.restaurant_id)
    }

    if (!qr_name) {
      setQrName(query.qr_name) // устанавливаем qr_name, если его нет
    }
  }, [])

  // Новый useEffect для отслеживания изменений в URL и localStorage
  useEffect(() => {
    const currentRestaurantId = query.restaurant_id
    const currentQrName = query.qr_name

    // Проверяем, изменились ли данные в URL или отличаются ли от localStorage
    const isRestaurantIdDifferent = currentRestaurantId && currentRestaurantId !== restaurant_id
    const isQrNameDifferent = currentQrName && currentQrName !== qr_name

    if (isRestaurantIdDifferent || isQrNameDifferent) {
      // Обнуляем все данные в localStorage при различии
      console.log(`Clearing localStorage due to differences in restaurant_id or qr_name`)

      // Очищаем все данные в localStorage
      setFoods([])
      setCartItems([])
      setTotalPrice(0)
      setComment("")
      setCustomerName("")
      setAddress("")
      setTelephone("")
      setOptionDelivery("on_site")
      setPaymentMethod("")

      // Устанавливаем новые значения в localStorage из URL
      if (isRestaurantIdDifferent) {
        setRestaurant_id(currentRestaurantId)
        console.log(`Updated restaurant_id to ${currentRestaurantId} from URL`)
      }

      if (isQrNameDifferent) {
        setQrName(currentQrName)
        console.log(`Updated qr_name to ${currentQrName} from URL`)
      }
    }
  }, [location.search]) // этот эффект будет срабатывать при изменении адресной строки

  useEffect(() => {
    if (restaurant_id) {
      setLoading(true)
      console.log(`Fetching data for restaurant_id: ${restaurant_id}`)

      getReastaurant(restaurant_id)
        .then((data) => {
          console.log("Restaurant fetched:", data)
          setRestaurant(data[0])
        })
        .catch(() => setLoading(false))
        .finally(() => setLoading(false))

      getSettings(restaurant_id)
        .then((data) => {
          console.log("Settings fetched:", data)
          // Если data - массив, берем первый элемент, иначе сам data
          const settingsData = Array.isArray(data) ? data[0] : data
          setSettings(settingsData)
        })
        .catch(() => setLoading(false))
        .finally(() => setLoading(false))

      getDishes(restaurant_id)
        .then((data) => {
          console.log("Dishes fetched:", data)
          setFoods(data)
        })
        .catch(() => setLoading(false))
        .finally(() => setLoading(false))

      getTypes(restaurant_id)
        .then((data) => {
          console.log("Types fetched:", data)
          setTypesList(data)
        })
        .catch(() => setLoading(false))
        .finally(() => setLoading(false))

      getGroups(restaurant_id)
        .then((data) => {
          console.log("Groups fetched:", data)
          setGroupsList(data)
        })
        .catch(() => setLoading(false))
        .finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [restaurant_id]) // добавляем зависимость от restaurant_id

  const handleSelectRestaurant = (restaurantId) => {
    setRestaurant_id(restaurantId)
    navigate(`/?restaurant_id=${restaurantId}`)
  }

  const isDevMode = process.env.REACT_APP_STAGE === "local"

  if (!restaurant_id && !loading) {
    if (isDevMode) {
      return <RestaurantSelector onSelectRestaurant={handleSelectRestaurant} />
    } else {
      return <NotFoundRestaurant />
    }
  }

  return (
    <>
      {loading && <LoadingOverlay />}

      <CartContext.Provider
        value={{
          typesList,
          setTypesList,
          groupsList,
          setGroupsList,
          foods,
          setFoods,
          cartItems,
          setCartItems,
          queryId,
          setQueryId,
          totalPrice,
          setTotalPrice,
          comment,
          setComment,
          customerName,
          setCustomerName,
          address,
          setAddress,
          telephone,
          setTelephone,
          optionDelivery,
          setOptionDelivery,
          user,
          paymentMethod,
          settings,
          language,
          setLanguage,
          restaurant_id,
        }}
      >
        <ThemeProvider theme={theme}>
          <PhoneSimulator>
            <Box
              className="App"
              sx={{
                paddingBottom: cartItems?.length > 0 ? "100px" : "0px",
                boxShadow: "0px 0px 10px 10px rgb(30 36 90 / 92%)",
              }}
            >
              <Header />
              <Routes>
                <Route path={"/"} element={<ProductsPage />} />
                <Route path={"product"} element={
                  <ProtectedRoute>
                    <Product />
                  </ProtectedRoute>
                } />
                <Route path={"product/:id"} element={
                  <ProtectedRoute>
                    <Product />
                  </ProtectedRoute>
                } />
                <Route path={"order"} element={
                  <ProtectedRoute>
                    <OrderPage />
                  </ProtectedRoute>
                } />
                <Route path={"payments"} element={
                  <ProtectedRoute>
                    <Payments />
                  </ProtectedRoute>
                } />
              </Routes>
            </Box>
          </PhoneSimulator>
        </ThemeProvider>
      </CartContext.Provider>

      {/* включить когда будут клиенты и на определенные фичи */}
      {/* {!restaurant.is_subscription_active ? <NoAccessMessage /> : null} */}
    </>
  )
}
